// Module 19 (BL-380) G9a - Guest Transport: plain helpers (no React, no database). The event's shuttle needs become ordinary Dispatch
// trips in the same company account; these helpers shape the request form and describe the linked trips.

export const ADDRESS_MIN = 3;
export const ADDRESS_MAX = 500;
export const NOTES_MAX = 2000;
export const PASSENGERS_MAX = 500;

// Dispatch statuses in plain words (the trip is an ordinary ride, so these are Dispatch's own statuses).
const STATUS_WORDS = {
    requested: 'Requested',
    confirmed: 'Confirmed',
    scheduled: 'Scheduled',
    in_progress: 'In progress',
    completed: 'Completed',
    closed: 'Closed',
    canceled: 'Canceled',
    no_show: 'No-show',
};
export function tripStatusLabel(status) {
    return STATUS_WORDS[status] || 'Requested';
}
// Requested = waiting on Dispatch (amber); confirmed..completed = going ahead (green); canceled / no-show = grey. The word is always shown.
export function tripStatusTone(status) {
    if (['confirmed', 'scheduled', 'in_progress', 'completed', 'closed'].includes(status)) return 'green';
    if (['canceled', 'no_show'].includes(status)) return 'grey';
    return 'amber';
}

// A stable id for one request (the database uses it as the trip's id, so a double-click or a retry never makes two trips).
export function newRequestId() {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
    const hex = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16));
    hex[12] = '4';
    hex[16] = ((parseInt(hex[16], 16) & 3) | 8).toString(16);
    const s = hex.join('');
    return `${s.slice(0, 8)}-${s.slice(8, 12)}-${s.slice(12, 16)}-${s.slice(16, 20)}-${s.slice(20)}`;
}

// The form's starting values: the first company and service, drop-off = the hotel block address (else the venue), the event's date,
// and as many passengers as guests who need the shuttle (at least 1).
export function transportForm(context = {}) {
    const companies = Array.isArray(context.companies) ? context.companies : [];
    const services = Array.isArray(context.services) ? context.services : [];
    return {
        companyId: companies[0]?.id || '',
        serviceId: services.length === 1 ? services[0].id : '',
        pickup: '',
        dropoff: context.defaultDropoff || '',
        date: context.eventDate || '',
        time: '',
        passengers: String(Math.max(1, Math.min(PASSENGERS_MAX, Number(context.shuttleGuests) || 0))),
        notes: '',
    };
}

// The first problem with the form, in plain words, or '' (the database checks all of this again).
export function transportProblem(form) {
    if (!form.serviceId) return 'Choose a service for the trip.';
    const pickup = (form.pickup || '').trim();
    const dropoff = (form.dropoff || '').trim();
    if (pickup.length < ADDRESS_MIN || pickup.length > ADDRESS_MAX) return 'Enter a pickup address.';
    if (dropoff.length < ADDRESS_MIN || dropoff.length > ADDRESS_MAX) return 'Enter a drop-off address.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date || '')) return 'Choose a pickup date.';
    if (!/^\d{2}:\d{2}$/.test(form.time || '')) return 'Choose a pickup time.';
    const passengers = String(form.passengers ?? '').trim();
    if (!/^\d{1,3}$/.test(passengers) || Number(passengers) < 1 || Number(passengers) > PASSENGERS_MAX) {
        return `Passengers must be a whole number from 1 to ${PASSENGERS_MAX}.`;
    }
    if ((form.notes || '').length > NOTES_MAX) return 'Notes can be up to 2,000 characters.';
    return '';
}

// What is sent to m19_request_guest_transport (exactly the keys it accepts).
export function transportFields(form, companies = []) {
    const fields = {
        serviceId: form.serviceId,
        pickup: form.pickup.trim(),
        dropoff: form.dropoff.trim(),
        date: form.date,
        time: form.time,
        passengers: Number(String(form.passengers).trim()),
        notes: (form.notes || '').trim(),
    };
    // only name the company when there is a choice; otherwise the database picks the company's own transportation hub
    if (companies.length > 1 && form.companyId) fields.companyId = form.companyId;
    return fields;
}

// "Guest Transport: The Ride: ATL · 2 trips"
export function transportHeadline(trips = [], companies = []) {
    const names = [...new Set(trips.map((trip) => trip.company).filter(Boolean))];
    const company = names.length ? names.join(' / ') : (companies[0]?.label || '');
    const count = `${trips.length} ${trips.length === 1 ? 'trip' : 'trips'}`;
    return `Guest Transport: ${company ? `${company} · ` : ''}${count}`;
}

// "Sat, Jun 12 · 10:00 AM" in the company's time zone (falls back to the browser's when the zone is unknown).
export function tripWhen(iso, timeZone) {
    if (!iso) return 'Time to be set by Dispatch';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return 'Time to be set by Dispatch';
    const options = { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
    try {
        return date.toLocaleString('en-US', { ...options, timeZone: timeZone || undefined }).replace(/, (\d{1,2}:\d{2})/, ' · $1');
    } catch {
        return date.toLocaleString('en-US', options).replace(/, (\d{1,2}:\d{2})/, ' · $1');
    }
}

// Why a planner cannot request a trip (shown instead of the button), or '' when they can.
export function cannotRequestReason(context = {}) {
    if (context.canRequest || context.canEdit === false) return '';
    if (context.eventCanceled) return 'This event is canceled, so no new trips can be requested.';
    if (!context.hasBookingCompany) return 'Set up a transportation booking company in Brands & Locations before requesting guest transport.';
    if (!Array.isArray(context.companies) || context.companies.length === 0) return 'Ask your administrator for Dispatch rights to request trips.';
    if (!Array.isArray(context.services) || context.services.length === 0) return 'Add an active service in Settings › Services before requesting guest transport.';
    return 'You cannot request trips for this event.';
}
